import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, checkAuth } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user ? true : null
  );

  useEffect(() => {
    // If user data was passed from AuthCallback, we're authenticated
    if (location.state?.user) {
      setIsAuthenticated(true);
      return;
    }

    // If still loading initial auth check, wait
    if (loading) return;

    // If user is loaded from context, we're authenticated
    if (user) {
      setIsAuthenticated(true);
      return;
    }

    // Server-side verification
    const verifyAuth = async () => {
      try {
        await checkAuth();
      } catch (error) {
        console.error('Auth verification failed:', error);
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
      }
    };

    verifyAuth();
  }, [location.state, user, loading, checkAuth, navigate]);

  // Update authenticated state when user changes
  useEffect(() => {
    if (!loading) {
      setIsAuthenticated(!!user);
    }
  }, [user, loading]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (isAuthenticated === false) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Show loading while checking auth
  if (isAuthenticated === null || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse text-[#00FF41] text-xl tracking-widest mb-4">
            LOADING SYSTEM...
          </div>
          <div className="w-48 h-1 bg-[#003B00] mx-auto overflow-hidden">
            <div className="h-full bg-[#00FF41] animate-loading-bar"></div>
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (isAuthenticated === false) {
    return null;
  }

  // Authenticated - render children
  return children;
};

export default ProtectedRoute;
